import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'

function AddCardModal({ columnId, onClose }) {
  const { selectedBoardId } = useBoardsContext()
  const { createCard } = useBoardMutations(selectedBoardId)
  const [search, setSearch] = useState('')
  const [addedIds, setAddedIds] = useState({})

  const recentJobsQuery = useQuery({
    queryKey: ['kanbanRecentJobs'],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', '/kanban_boards/search_jobs', {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    }
  })

  const searchJobsQuery = useQuery({
    queryKey: ['kanbanSearchJobs', search],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_boards/search_jobs?query=${encodeURIComponent(search)}`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    },
    enabled: search.length >= 2
  })

  const isSearching = search.length >= 2
  const activeQuery = isSearching ? searchJobsQuery : recentJobsQuery
  const jobs = activeQuery.data?.jobs || []

  const handleAdd = useCallback(async (jobId) => {
    setAddedIds(prev => ({ ...prev, [jobId]: 'adding' }))
    try {
      await createCard.mutateAsync({
        job_id: jobId,
        kanban_column_id: columnId,
        row_order_position: 'first'
      })
      setAddedIds(prev => ({ ...prev, [jobId]: 'added' }))
      setTimeout(() => {
        setAddedIds(prev => ({ ...prev, [jobId]: null }))
      }, 1500)
    } catch {
      setAddedIds(prev => ({ ...prev, [jobId]: null }))
    }
  }, [createCard, columnId])

  return (
    <div className="kb-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        data-tour="add-card-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add Job Card</h3>
          <button className="text-gray-400 hover:text-gray-600 text-lg" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="p-4">
          <input
            type="text"
            className="form-input w-full rounded-md border-gray-300 shadow-sm text-sm mb-3"
            placeholder="Search jobs by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          {!isSearching && (
            <div className="text-xs text-gray-400 mb-2">Recently updated jobs</div>
          )}

          <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md">
            {activeQuery.isLoading && (
              <div className="p-3 text-sm text-gray-400 text-center">
                Loading...
              </div>
            )}

            {!activeQuery.isLoading && jobs.length === 0 && (
              <div className="p-3 text-sm text-gray-400 text-center">
                {isSearching ? 'No jobs found' : 'No recent jobs'}
              </div>
            )}

            {jobs.map(job => {
              const state = addedIds[job.id]
              return (
                <div
                  key={job.id}
                  className="flex items-center justify-between px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="text-sm font-medium text-gray-700 truncate">({job.custom_id}) {job.name}</div>
                    {job.status && (
                      <span className="text-xs text-gray-400">{job.status}</span>
                    )}
                  </div>
                  <button
                    className={`text-xs font-semibold px-3 py-1 rounded transition-colors flex-shrink-0 ${
                      state === 'added'
                        ? 'bg-green-100 text-green-700'
                        : state === 'adding'
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                    }`}
                    onClick={() => handleAdd(job.id)}
                    disabled={state === 'adding' || state === 'added'}
                    style={{ outline: 'none' }}
                  >
                    {state === 'added' ? 'Added!' : state === 'adding' ? '...' : 'Add'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            className="inline-flex justify-center rounded-md px-4 py-2 bg-gray-100 text-sm leading-5 font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-200 cursor-pointer"
            data-tour="add-card-done"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

AddCardModal.propTypes = {
  columnId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired
}

export default AddCardModal
